import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, Stage } from '@react-three/drei';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { MTLLoader } from 'three/addons/loaders/MTLLoader.js';
import { useEffect, useState, useRef, forwardRef, useCallback, memo, useImperativeHandle } from 'react';
import * as THREE from 'three';

// Enable Three.js internal caching for XHR requests
THREE.Cache.enabled = true;

import type { RelatedFile } from '../services/api.ts';

const MAX_CACHE_SIZE = 5;

type CacheEntry = { object: THREE.Group; textures: string[]; mtlStatus: boolean };
const MODEL_CACHE = new Map<string, CacheEntry>();

function disposeObject(obj: THREE.Group) {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((m: THREE.Material) => {
        Object.values(m).forEach((val) => {
          if (val instanceof THREE.Texture) val.dispose();
        });
        m.dispose();
      });
    }
  });
}

function addToCache(key: string, entry: CacheEntry) {
  if (MODEL_CACHE.size >= MAX_CACHE_SIZE) {
    const oldest = MODEL_CACHE.keys().next().value;
    if (oldest) {
      disposeObject(MODEL_CACHE.get(oldest)!.object);
      MODEL_CACHE.delete(oldest);
    }
  }
  MODEL_CACHE.set(key, entry);
}

interface ModelProps {
  objPath: string;
  objName?: string;
  mtlPath?: string;
  mtlName?: string;
  textureUrls?: RelatedFile[];
  onCameraFitted?: (position: THREE.Vector3, target: THREE.Vector3) => void;
  onTexturesLoaded?: (textures: string[]) => void;
  onModelBounds?: (minY: number) => void;
  onLoadError?: (error: string | null) => void;
  onMtlStatus?: (loaded: boolean | null) => void;
  onModelLoaded?: (model: THREE.Group | null) => void;
  setIsLoading?: (loading: boolean) => void;
  // Lighting/BG Props
  ambientColor?: string;
  ambientIntensity?: number;
  keyLightColor?: string;
  keyLightIntensity?: number;
  fillLightColor?: string;
  fillLightIntensity?: number;
  rimLightColor?: string;
  rimLightIntensity?: number;
  bgColor?: string;
}

const DEBUG = import.meta.env.DEV;

const ModelLoader = forwardRef<THREE.Group, ModelProps>((props, ref) => {
  const { objPath, objName, mtlPath, textureUrls = [], onCameraFitted, onTexturesLoaded, onLoadError, onMtlStatus, onModelLoaded, setIsLoading } = props;
  const [object, setObject] = useState<THREE.Group | null>(null);
  const { camera, controls } = useThree();

  // Handle Google Drive URLs via Backend Proxy
  const getDirectUrl = (url: string | undefined) => {
    if (!url) return '';
    
    if (url.includes('drive.google.com')) {
      const fileId = new URL(url).searchParams.get('id');
      if (fileId) {
        const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        if (DEBUG) console.log(`Routing download for ${fileId} through backend proxy`);
        return `${apiBase}/download/${fileId}`;
      }
    }
    return url;
  };

  useEffect(() => {
    const loadModel = async () => {
      if (!objPath) {
        if (setIsLoading) setIsLoading(false);
        setObject(null);
        return;
      }

      // Check cache first
      if (MODEL_CACHE.has(objPath)) {
        if (DEBUG) console.log(`🚀 Loading model from cache: ${objName}`);
        const cached = MODEL_CACHE.get(objPath)!;
        
        if (onMtlStatus) onMtlStatus(cached.mtlStatus);
        if (onTexturesLoaded) onTexturesLoaded(cached.textures);
        if (onLoadError) onLoadError(null);
        if (onModelLoaded) onModelLoaded(cached.object);
        
        setObject(cached.object);
        setTimeout(() => fitCamera(cached.object), 0);
        return;
      }

      try {
        if (setIsLoading) setIsLoading(true);
        if (onLoadError) onLoadError(null);
        if (onMtlStatus) onMtlStatus(null);
        
        let materials: any = null;
        let mtlLoadedStatus = false;
        let matchedTextures: string[] = [];

        if (mtlPath) {
          try {
            const mtlDirectUrl = getDirectUrl(mtlPath);
            const mtlResponse = await fetch(mtlDirectUrl);
            if (!mtlResponse.ok) throw new Error(`Failed to fetch MTL: ${mtlResponse.status}`);
            let mtlText = await mtlResponse.text();

            // Texture mapping logic
            const textureRefRegex = /(map_[A-Za-z]+|map|bump|refl|disp|norm)\s+([^\r\n]+)/gi;
            const modelName = (objName || '').split('.')[0];
            const cleanModelBase = modelName.split('(')[0].trim().toLowerCase();

            let match;
            const refs = [];
            while ((match = textureRefRegex.exec(mtlText)) !== null) {
              refs.push({
                fullMatch: match[0],
                keyword: match[1],
                originalPath: match[2].trim().replace(/^["']|["']$/g, '')
              });
            }

            let updatedMtlText = mtlText;
            refs.forEach(ref => {
              const originalFile = ref.originalPath.split(/[/\\]/).pop() || '';
              const originalBase = originalFile.split('.')[0].toLowerCase();
              
              let bestMatch = 
                textureUrls.find(t => t.name.toLowerCase() === originalFile.toLowerCase()) ||
                textureUrls.find(t => t.name.toLowerCase().includes(originalBase)) ||
                textureUrls.find(t => originalBase.includes(t.name.toLowerCase().split('.')[0]));
              
              const isStrictMatch = !!bestMatch;
              
              if (!bestMatch) {
                bestMatch = 
                  textureUrls.find(t => t.name.toLowerCase().startsWith(cleanModelBase)) ||
                  textureUrls.find(t => t.name.toLowerCase().includes(cleanModelBase));
              }

              if (!bestMatch && textureUrls.length === 1) bestMatch = textureUrls[0];

              if (bestMatch) {
                const texDirectUrl = getDirectUrl(bestMatch.url);
                const escapedPath = ref.originalPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const replaceRegex = new RegExp(`${ref.keyword}\\s+(?:[./\\\\]+)?${escapedPath}`, 'gi');
                updatedMtlText = updatedMtlText.replace(replaceRegex, `${ref.keyword} ${texDirectUrl}`);
                
                if (isStrictMatch && !matchedTextures.includes(bestMatch.name)) {
                  matchedTextures.push(bestMatch.name);
                }
              }
            });

            mtlText = updatedMtlText;
            const mtlLoader = new MTLLoader();
            mtlLoader.setMaterialOptions({ normalizeRGB: true, ignoreZeroRGBs: false });
            materials = mtlLoader.parse(mtlText, '');
            materials.preload();
            mtlLoadedStatus = true;
            if (onMtlStatus) onMtlStatus(true);
          } catch (mtlError) {
            console.warn('MTL failed:', mtlError);
            if (onMtlStatus) onMtlStatus(false);
          }
        }

        const obj = await new Promise<THREE.Group>((resolve, reject) => {
          const loader = new OBJLoader();
          if (materials) loader.setMaterials(materials);
          loader.load(getDirectUrl(objPath), resolve, undefined, reject);
        });

        if (onTexturesLoaded) onTexturesLoaded(matchedTextures);

        addToCache(objPath, {
          object: obj,
          textures: matchedTextures,
          mtlStatus: mtlLoadedStatus
        });

        setObject(obj);
        fitCamera(obj);
        if (onModelLoaded) onModelLoaded(obj);
        if (setIsLoading) setIsLoading(false);

      } catch (error) {
        console.error('Error loading model:', error);
        if (setIsLoading) setIsLoading(false);
        if (onLoadError) onLoadError(error instanceof Error ? error.message : 'Failed to load model');
      }
    };

    const fitCamera = (obj: THREE.Group) => {
      const box = new THREE.Box3().setFromObject(obj);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 1.5;

      camera.position.set(center.x, center.y + distance * 0.5, center.z + distance);
      camera.lookAt(center);
      
      if (controls) {
        (controls as any).target.copy(center);
        (controls as any).update();
      }

      if (onCameraFitted) onCameraFitted(camera.position.clone(), center.clone());
    };

    loadModel();
  }, [objPath, mtlPath, camera, controls]);

  return object ? <primitive ref={ref} object={object} /> : null;
});

const My3DViewer = forwardRef((props: ModelProps, ref) => {
  const { 
    objPath, objName, mtlPath, mtlName, textureUrls,
    ambientColor, ambientIntensity, keyLightColor, keyLightIntensity,
    fillLightColor, fillLightIntensity, rimLightColor, rimLightIntensity,
    bgColor, setIsLoading, onLoadError, onMtlStatus, onTexturesLoaded, onModelLoaded
  } = props;
  
  const controlsRef = useRef<any>(null);
  const [fittedCameraPos, setFittedCameraPos] = useState<THREE.Vector3 | null>(null);
  const [fittedTarget, setFittedTarget] = useState<THREE.Vector3 | null>(null);
  const modelRef = useRef<THREE.Group>(null);
  const [internalLoading, setInternalLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    resetView: () => {
      if (fittedCameraPos && fittedTarget && controlsRef.current) {
        controlsRef.current.object.position.copy(fittedCameraPos);
        controlsRef.current.target.copy(fittedTarget);
        controlsRef.current.update();
      }
    }
  }));

  const handleCameraFitted = useCallback((position: THREE.Vector3, target: THREE.Vector3) => {
    setFittedCameraPos(position);
    setFittedTarget(target);
    setInternalLoading(false);
  }, []);

  return (
    <div style={{ height: "100vh", width: "100%", position: 'relative' }}>
      {internalLoading && objPath && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <div style={{ color: 'white', marginTop: '80px', position: 'absolute' }}>
            Loading Model...
          </div>
        </div>
      )}
      
      <Canvas camera={{ fov: 50, far: 10000 }}>
        <color attach="background" args={[bgColor || '#828282']} />
        <ambientLight color={ambientColor} intensity={ambientIntensity} />
        <directionalLight color={keyLightColor} position={[10, 10, 5]} intensity={keyLightIntensity} />
        <directionalLight color={fillLightColor} position={[-10, 10, 5]} intensity={fillLightIntensity} />
        <directionalLight color={rimLightColor} position={[0, -10, -10]} intensity={rimLightIntensity} />
        <Stage intensity={0.5} environment="city" adjustCamera={false}>
          <Grid position={[0, 0, 0]} args={[500, 100]} cellColor="cyan" sectionColor="#333333" />
          <ModelLoader
            ref={modelRef}
            objPath={objPath}
            objName={objName}
            mtlPath={mtlPath}
            mtlName={mtlName}
            textureUrls={textureUrls}
            onCameraFitted={handleCameraFitted}
            onTexturesLoaded={onTexturesLoaded}
            onLoadError={onLoadError}
            onMtlStatus={onMtlStatus}
            onModelLoaded={onModelLoaded}
            setIsLoading={(l) => {
              setInternalLoading(l);
              if (setIsLoading) setIsLoading(l);
            }}
          />
        </Stage>
        <OrbitControls
          ref={controlsRef}
          makeDefault
          enableDamping
          dampingFactor={0.05}
          rotateSpeed={0.5}
          panSpeed={0.8}
          zoomSpeed={0.8}
          mouseButtons={{
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.PAN
          }}
        />
      </Canvas>
    </div>
  );
});

export default memo(My3DViewer);
